import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { DefaultLayout } from "../../../../layouts/Default";
import { Main } from "../../../../themes/Default/components/Main";
import { Tabs } from "../../../../themes/Default/components/Tabs";
import { TopNavigation } from "../../../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../../../themes/Default/components/Breadcrumps";
import { Select } from "../../../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../../../services/core/Select";
import { Link } from "../../../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../../../themes/Default/components/Form";
import { Row } from "../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../themes/Default/components/Form/components/Row/components/Field";
import { studentsManagement } from "../../../../hooks/api/StudentsManagement";
import { useTranslation } from "react-i18next";
import { Title } from "../../../../themes/Default/components/Title";
import { legalEntityManagement } from "../../../../hooks/api/LegalEntitiesManagement/LegalEntitiesManagement";
import { Input } from "../../../../themes/Default/components/Form/components/Input";
import { toast } from "react-toastify";
import { Toaster } from "../../../../themes/Default/components/Toaster";

const ViewContract = () => {
    const { t } = useTranslation("common");
    const { studentId } = useParams();
    const { contractId } = useParams();
    const formRef = useRef(null);
    const [legalEntities, setLegalEntities] = useState([]);
    const [studentFullName, setStudentFullName] = useState([]);
    const navigate = useNavigate();
    const { useViewContractMutation, useViewStudentsMutation, useResendContractMutation } = studentsManagement;
    const { useLegalEntityListMutation } = legalEntityManagement;
    const [viewContract] = useViewContractMutation();
    const [resendContract] = useResendContractMutation();
    const [isArchived, setIsArchived] = useState(true);

    const [legalEntityList] = useLegalEntityListMutation();
    const [viewStudent] = useViewStudentsMutation();

    const [installmentDate, setInstallmentDate] = useState([]);
    const [paymentStartMonth, setPaymentStartMonth] = useState([]);

    const [groups, setGroups] = useState([]);

    const tabs = [
        { title: t("details"), onClick: () => gotoDetailsTab(studentId) },
        // { title: t("students.tests"), onClick: () => gotoTestsTab(studentId)},
        { title: t("students.contract"), isActive: true },
        { title: t("calls.title"), onClick: () => gotoCallsTab(studentId) },
        { title: t("groups.title"), onClick: () => gotoGroupsTab(studentId) },
        { title: t("students.certificates"), onClick: () => gotoCertificatesTab(studentId) },
        { title: t("documents"), onClick: () => gotoDocumentsTab(studentId) },
        { title: t("notes"), onClick: () => gotoNotesTab(studentId) },
    ];

    const gotoTestsTab = (studentId) => {
        navigate("/students/" + studentId + "/tests");
    };

    const gotoCallsTab = (studentId) => {
        navigate("/students/" + studentId + "/calls");
    };

    const gotoCertificatesTab = (studentId) => {
        navigate("/students/" + studentId + "/certificates");
    };

    const gotoDetailsTab = (studentId) => {
        navigate("/students/edit/" + studentId);
    };

    const gotoNotesTab = (studentId) => {
        navigate("/students/" + studentId + "/notes");
    };

    const gotoDocumentsTab = (studentId) => {
        navigate("/students/" + studentId + "/documents");
    };

    const gotoGroupsTab = (studentId) => {
        navigate("/students/" + studentId + "/groups");
    };

    const resend = () => {
        toast.warn(
            ({ closeToast }) => (
                <Toaster
                    closeToast={closeToast}
                    description={t("areYouSure")}
                    optionNo={t("no")}
                    optionYes={t("yes")}
                    onClick={() => {
                        closeToast();
                        resendContract({ studentId, contractId }).then((result) => {
                            if (result?.error) {
                                toast.error(result?.error?.data?.error?.responseMessage);
                            } else {
                                toast.info(result?.data?.info?.responseMessage);
                            }
                        });
                    }}
                />
            ),
            {
                autoClose: false,
                progress: false,
                closeButton: false,
            },
        );
    };

    const installmentDates = () => {
        const options = [
            { id: 5, name: "5" },
            { id: 10, name: "10" },
            { id: 15, name: "15" },
            { id: 20, name: "20" },
            { id: 25, name: "25" },
        ];
    
        return prepareSelect(options);
    };
    const paymentStartMonths = () => {
        const options = [
            { id: 1, name: "Januar" },
            { id: 2, name: "Februar" },
            { id: 3, name: "Mart" },
            { id: 4, name: "April" },
            { id: 5, name: "Maj" },
            { id: 6, name: "Jun" },
            { id: 7, name: "Jul" },
            { id: 8, name: "Avgust" },
            { id: 9, name: "Septembar" },
            { id: 10, name: "Oktobar" },
            { id: 11, name: "Novembar" },
            { id: 12, name: "Decembar" }
        ];
    
        return prepareSelect(options);
    };

    useEffect(() => {
        viewContract({ studentId, contractId }).then((result) => {
            formRef.current.setValues(result?.data);
            setIsArchived(result?.data?.isArchived);
        });

        legalEntityList().then((result) => {
            setLegalEntities(prepareSelect(result?.data?.records));
        });

        viewStudent(studentId).then((result) => {
            setStudentFullName(result?.data?.firstName + ' ' + result?.data?.lastName);
            setGroups(result?.data?.activeGroups);
        });

        setInstallmentDate(installmentDates);
        setPaymentStartMonth(paymentStartMonths);
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to={'/students/' + studentId + '/contracts'} />
                <Button title={t("students.resendContract")} icon="fa-regular fa-paper-plane" onClick={resend} isDisabled={isArchived}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Title>{studentFullName}</Title>
                <Breadcrumps>
                    <Link title={t("students.title")} icon="fa-user-graduate" />
                    <Link title={t("students.contract")} />
                    <Link title={t("details")} isActive={true}/>
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        location: null,
                        language: null,
                        legalEntity: null,
                        groupType: null,
                        totalAmount: null,
                        numberOfInstallments: null,
                        installmentDates: null,
                        paymentStartMonth: ""
                    }}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select 
                                        name="group" 
                                        label={t('Grupa')} 
                                        onChange={handleChange} 
                                        required={true} 
                                        options={groups} 
                                        selectedValue={values.group}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Select 
                                        name="legalEntity" 
                                        label={t('students.contractLegalEntity')} 
                                        onChange={handleChange} 
                                        required={true} 
                                        options={legalEntities} 
                                        selectedValue={values.legalEntity} 
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="totalAmount"
                                        label={t("students.contractTotalAmount")}
                                        required={true}
                                        value={values.totalAmount + "RSD"}
                                        onChange={handleChange}
                                        disabled={true}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="numberOfInstallments"
                                        label={t("students.contractNumberOfInstallments")}
                                        required={true}
                                        value={values.numberOfInstallments}
                                        onChange={handleChange}
                                        disabled={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select 
                                        name="installmentDates" 
                                        label={t('students.contractInstallmentDates')}
                                        onChange={handleChange} 
                                        options={installmentDate} 
                                        selectedValue={values.installmentDates}
                                        disabled={true}
                                        required={true}
                                    />
                                </Field>
                                <Field>
                                    <Select 
                                        name="paymentStartMonth" 
                                        label={t('students.contractPaymentStartMonth')} 
                                        onChange={handleChange} 
                                        options={paymentStartMonth} 
                                        selectedValue={values.paymentStartMonth} 
                                        disabled={true}
                                        required={true}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default ViewContract;
